import React, { useState, useMemo, useEffect } from 'react';
import { NodeType } from '../types.js';
import { NODE_TYPES } from '../constants.js';
import { AIGenerateIcon, SearchIcon, StarIcon, StarOutlineIcon, CloseIcon } from './icons';

const nodeCategoriesRaw = [
    {
        name: 'Flow Control',
        nodes: [NodeType.TRIGGER, NodeType.IF_CONDITION, NodeType.LOOP_START, NodeType.LOOP_END, NodeType.WAIT]
    },
    {
        name: 'Integrations',
        nodes: [NodeType.SEND_EMAIL, NodeType.SEND_MESSAGE, NodeType.UPDATE_CRM, NodeType.SOCIAL_POST, NodeType.QUERY_DATABASE, NodeType.CONNECT_API]
    },
    {
        name: 'Busin.US', // Renamed from Business Automation
        nodes: [NodeType.REGISTER_BUSINESS, NodeType.HIRE_EMPLOYEE, NodeType.EXPAND_BUSINESS, NodeType.CREATE_WEBSITE, NodeType.GENERATE_DOCUMENT, NodeType.MANAGE_INVOICES, NodeType.FILE_TAXES, NodeType.COMPLIANCE_CHECK]
    },
    {
        name: 'AI & Data',
        nodes: [NodeType.BROWSING_AGENT, NodeType.DASHBOARD, NodeType.MARKET_RESEARCH]
    },
    {
        name: 'Developer',
        nodes: [NodeType.CODE, NodeType.LOGGER]
    },
    // Add Documentation category for COMMENT and TODO nodes
    {
        name: 'Documentation',
        nodes: [NodeType.COMMENT, 'TODO_LIST', 'TODO_VIEW']
    }
];

// Pin Busin.US category to the top
const businUsIndex = nodeCategoriesRaw.findIndex(c => c.name === 'Busin.US');
const businUsCategory = nodeCategoriesRaw.splice(businUsIndex, 1)[0];

// Pin Documentation category to second position
const documentationIndex = nodeCategoriesRaw.findIndex(c => c.name === 'Documentation');
const documentationCategory = nodeCategoriesRaw.splice(documentationIndex, 1)[0];

const sortedNodeCategories = [businUsCategory, documentationCategory, ...nodeCategoriesRaw];

const FAVORITES_STORAGE_KEY = 'busin.us-favorite-nodes';


const AiNodeCreator = ({ onAiAddNode, aiPrompt, setAiPrompt, businessPlanText = '' }) => { // Provide default value
    const [isLoading, setIsLoading] = useState(false);

    // Update AI prompt when business plan text changes
    useEffect(() => {
        const businessPlanTextValue = businessPlanText || ''; // Ensure we have a string
        const aiPromptValue = aiPrompt || ''; // Ensure we have a string
        if (businessPlanTextValue && !aiPromptValue) {
            setAiPrompt(businessPlanTextValue);
        }
    }, [businessPlanText, aiPrompt, setAiPrompt]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const aiPromptValue = aiPrompt || ''; // Ensure we have a string
        if (!aiPromptValue.trim() || isLoading) return;
        setIsLoading(true);
        await onAiAddNode(aiPromptValue);
        setIsLoading(false);
        // Don't clear the prompt here, it will be cleared in the parent component
    };

    return (
        <div className="mb-4">
            <h3 className="text-md font-semibold text-dark-text-primary mb-2">Create new node with AI</h3>
            <form onSubmit={handleSubmit} className="relative">
                <input
                    type="text"
                    value={aiPrompt || ''} // Ensure we have a string
                    onChange={(e) => setAiPrompt(e.target.value || '')} // Ensure we have a string
                    placeholder="e.g., 'host a conference'"
                    className="w-full bg-dark-bg border border-dark-border rounded-md py-2 pl-3 pr-10 text-sm text-dark-text-primary placeholder-dark-text-secondary focus:ring-2 focus:ring-brand-primary focus:outline-none transition"
                    disabled={isLoading}
                />
                <button
                    type="submit"
                    disabled={isLoading || !aiPrompt || !aiPrompt.trim()} // Added null check
                    className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-dark-text-secondary hover:bg-brand-primary hover:text-white disabled:hover:bg-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Generate Node"
                >
                    {isLoading ? (
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    ) : (
                        <AIGenerateIcon className="w-4 h-4" />
                    )}
                </button>
            </form>
        </div>
    );
};

export const Sidebar = ({ onAddNode, onAiAddNode, isMobile, isSidebarOpen, setIsSidebarOpen, aiPrompt, setAiPrompt, businessPlanText = '' }) => { // Provide default value
  const [searchTerm, setSearchTerm] = useState('');
  const [favoriteNodes, setFavoriteNodes] = useState(() => {
    try {
        const savedFavorites = localStorage.getItem(FAVORITES_STORAGE_KEY);
        return savedFavorites ? JSON.parse(savedFavorites) : [];
    } catch (e) {
        console.error("Failed to load favorite nodes from localStorage", e);
        return [];
    }
  });

  useEffect(() => {
    try {
        localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favoriteNodes));
    } catch (e) {
        console.error("Failed to save favorite nodes to localStorage", e);
    }
  }, [favoriteNodes]);

  const toggleFavorite = (nodeType) => {
    setFavoriteNodes(prev => 
        prev.includes(nodeType)
            ? prev.filter(t => t !== nodeType)
            : [...prev, nodeType]
    );
  };

  const filteredCategories = useMemo(() => {
    const lowercasedFilter = searchTerm.toLowerCase().trim();
    
    let allCategories = [...sortedNodeCategories];

    if (favoriteNodes.length > 0) {
        allCategories.unshift({
            name: 'Favorites',
            nodes: favoriteNodes
        });
    }

    if (!lowercasedFilter) {
      return allCategories.map(category => ({
        ...category,
        nodes: category.nodes.map(nodeType => NODE_TYPES[nodeType] || NODE_TYPES.NOVEL_NODE)
      }));
    }

    return allCategories
      .map(category => {
        const filteredNodes = category.nodes
          .map(nodeType => NODE_TYPES[nodeType] || NODE_TYPES.NOVEL_NODE)
          .filter(nodeInfo =>
            nodeInfo.label.toLowerCase().includes(lowercasedFilter) ||
            nodeInfo.description.toLowerCase().includes(lowercasedFilter)
          );
        return { ...category, nodes: filteredNodes };
      })
      .filter(category => category.nodes.length > 0);
  }, [searchTerm, favoriteNodes]);

  // For mobile, we render a modal-like sidebar
  if (isMobile) {
    return (
      <>
        {/* Mobile sidebar overlay */}
        {isSidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 sm:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}
        
        {/* Mobile sidebar */}
        <aside 
          className={`fixed top-0 left-0 h-full bg-dark-surface border-r border-dark-border p-4 z-50 flex flex-col transition-transform duration-300 ease-in-out sm:hidden ${
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          style={{ width: '90%', maxWidth: '320px' }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-dark-text-primary">Nodes</h2>
            <button 
              onClick={() => setIsSidebarOpen(false)}
              className="p-2 rounded-md text-dark-text-secondary hover:bg-dark-bg"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>
          
          <AiNodeCreator onAiAddNode={onAiAddNode} aiPrompt={aiPrompt} setAiPrompt={setAiPrompt} businessPlanText={businessPlanText} />
          <div className="w-full h-px bg-dark-border my-4"></div>
          
          <div className="relative mb-3">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-text-secondary" />
            <input
              type="text"
              placeholder="Search nodes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-dark-bg border border-dark-border rounded-md py-2 pl-9 pr-3 text-sm text-dark-text-primary placeholder-dark-text-secondary focus:ring-2 focus:ring-brand-primary focus:outline-none transition"
            />
          </div>
          
          <div className="flex-grow overflow-y-auto -mr-2 pr-2">
            {filteredCategories.map((category) => (
              <div key={category.name}>
                <h3 className="text-xs font-bold uppercase text-dark-text-secondary tracking-wider mt-4 mb-2 px-1">
                  {category.name}
                </h3>
                <div className="space-y-2">
                  {category.nodes.map((nodeInfo) => (
                    <button
                      key={nodeInfo.type}
                      onClick={() => onAddNode(nodeInfo.type)}
                      className="w-full flex items-center gap-3 p-3 bg-dark-bg border border-dark-border rounded-md hover:bg-dark-surface transition-colors text-left"
                    >
                      <div className="flex-shrink-0 p-2 rounded-md bg-dark-surface text-brand-secondary">
                        {nodeInfo.icon || <div className="w-5 h-5 bg-gray-500 rounded-md"></div>}
                      </div>
                      <div className="overflow-hidden">
                        <div className="font-medium text-sm text-dark-text-primary truncate">{nodeInfo.label}</div>
                        <div className="text-xs text-dark-text-secondary truncate">{nodeInfo.description}</div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(nodeInfo.type);
                        }}
                        className="ml-auto p-1 text-dark-text-secondary hover:text-brand-primary"
                      >
                        {favoriteNodes.includes(nodeInfo.type) ? (
                          <StarIcon className="w-4 h-4 fill-current" />
                        ) : (
                          <StarOutlineIcon className="w-4 h-4" />
                        )}
                      </button>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </aside>
      </>
    );
  }

  // Desktop sidebar
  return (
    <aside className="w-80 bg-dark-surface border-r border-dark-border p-4 flex flex-col flex-shrink-0">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-dark-text-primary">Nodes</h2>
      </div>
      
      <AiNodeCreator onAiAddNode={onAiAddNode} aiPrompt={aiPrompt} setAiPrompt={setAiPrompt} businessPlanText={businessPlanText} />
      <div className="w-full h-px bg-dark-border my-4"></div>
      
      <div className="relative mb-4">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-text-secondary" />
        <input
          type="text"
          placeholder="Search nodes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-dark-bg border border-dark-border rounded-md py-2 pl-9 pr-3 text-sm text-dark-text-primary placeholder-dark-text-secondary focus:ring-2 focus:ring-brand-primary focus:outline-none transition"
        />
      </div>
      
      <div className="flex-grow overflow-y-auto -mr-2 pr-2">
        {filteredCategories.map((category) => (
          <div key={category.name}>
            <h3 className="text-xs font-bold uppercase text-dark-text-secondary tracking-wider mt-4 mb-2 px-1">
              {category.name}
            </h3>
            <div className="space-y-2">
              {category.nodes.map((nodeInfo) => (
                <button
                  key={nodeInfo.type}
                  onClick={() => onAddNode(nodeInfo.type)}
                  className="w-full flex items-center gap-3 p-3 bg-dark-bg border border-dark-border rounded-md hover:bg-dark-surface transition-colors text-left"
                >
                  <div className="flex-shrink-0 p-2 rounded-md bg-dark-surface text-brand-secondary">
                    {nodeInfo.icon || <div className="w-5 h-5 bg-gray-500 rounded-md"></div>}
                  </div>
                  <div className="overflow-hidden">
                    <div className="font-medium text-sm text-dark-text-primary truncate">{nodeInfo.label}</div>
                    <div className="text-xs text-dark-text-secondary truncate">{nodeInfo.description}</div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(nodeInfo.type);
                    }}
                    className="ml-auto p-1 text-dark-text-secondary hover:text-brand-primary"
                  >
                    {favoriteNodes.includes(nodeInfo.type) ? (
                      <StarIcon className="w-4 h-4 fill-current" />
                    ) : (
                      <StarOutlineIcon className="w-4 h-4" />
                    )}
                  </button>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
};